import { createAction } from 'redux-actions';
import { timeMinute, timeMonth, timeYear } from 'd3-time';
import { utcFormat, utcParse } from 'd3-time-format';

import {
  descriptionCreator,
  idCreator,
  metaCreator,
  payloadCreator,
} from './creator';
import { checkForCachedDataAction, requestAction } from './request';
import fetch, {
  Api,
  defaultRequestConfig,
  getJson,
  handleError,
} from './fetch';
import activityTypesWellmo from '../utils/activityTypesWellmo.json';

const wellmoTimeFormat = utcFormat('%Y%m%d%H%M%S%L');
const wellmoTimeParse = utcParse('%Y%m%d%H%M%S%L');

export const wellmoFetchAction = createAction('GET_WELLMO_DATA', payloadCreator, metaCreator);

const collections = {
  bloodPressure: {
    key: 'bloodpressure',
  },
  exercises: {
    key: 'exercise',
  },
  sleep: {
    key: 'sleep',
  },
  steps: {
    key: 'steps',
  },
  weight: {
    key: 'weight',
  },
};

export const supportedTypes = Object.keys(collections);

const getItemAdapter = type => (item) => {
  const parsed = {
    id: `wellmo${item.id}`,
    raw: item,
    source: 'wellmo',
    time: wellmoTimeParse(item.time).toISOString(),
  };
  switch (type) {
    case 'bloodPressure':
      parsed.diastolic = item.data.diastolic;
      parsed.systolic = item.data.systolic;
      break;
    case 'exercises':
      parsed.duration = item.data.duration;
      parsed.intensity = item.data.intensity;
      parsed.type = activityTypesWellmo[item.data.type];
      if (!parsed.type) {
        console.warn('Unmapped Wellmo activity type', item.data.type);
        parsed.type = item.data.type; // NOT MAPPED!!
      }
      break;
    case 'sleep':
      parsed.duration = item.data.sleep;
      break;
    case 'steps':
      parsed.steps = item.data.runningSteps + item.data.walkingSteps;
      // TODO: if item.data.runningAsMeters > item.data.walkingAsMeters --> As running exercise!
      break;
    case 'weight':
      parsed.weight = item.data.weight;
      break;
    default:
  }
  parsed.endTime = wellmoTimeParse(item.endTime) || timeMinute.offset(parsed.time, parsed.duration);
  return parsed;
};

const getUrl = ({ type, params }) => {
  const startTimeString = params.startTime
    ? `?measureStart=${wellmoTimeFormat(params.startTime)}`
    : '';
  const endTimeString = params.endTime
    ? `${startTimeString.length ? '&' : '?'}measureEnd=${wellmoTimeFormat(params.endTime)}`
    : '';
  const url = `${Api}/wellmo/rest/user/wellnessdata/name/${
    collections[type].key
  }${
    startTimeString
  }${
    endTimeString
  }`;
  return Promise.resolve(url);
};

const fetchData = ({ type, params }) => getUrl({ type, params })
  .then(url => fetch(url, defaultRequestConfig))
  .then(handleError)
  .then(getJson)
  .then(json => ({ [type]: json.map(getItemAdapter(type)) }))
  .catch(error => Promise.reject(error));

const now = new Date();
const defaultParams = { startTime: timeMonth(now) };

export const getWellmoData = ({ type, params = defaultParams }) => ((dispatch) => {
  // We extend the fetch for some data to years!
  let modifiedParams = params;
  if (type === 'bloodPressure' || type === 'weight') {
    const startTime = timeYear(timeYear.offset(params.startTime, params.endTime ? -1 : 0));
    const endTime = new Date(Math.min(timeYear.offset(startTime, params.endTime ? 2 : 1), now));
    modifiedParams = {
      ...params,
      startTime,
      endTime,
    };
  }
  const id = idCreator();
  const description = descriptionCreator({ source: 'Wellmo', type, params: modifiedParams });
  return checkForCachedDataAction(requestAction({ id, description }), dispatch)
    .then(() => fetchData({ type, params: modifiedParams }))
    .then(data => dispatch(wellmoFetchAction({ id, description, data })))
    .catch(error => dispatch(wellmoFetchAction({ id, description, error })));
});

export default getWellmoData;
